// StakeholderVerification.jsx
import React from 'react';
import { Users, Building2, Truck, UserCheck, Shield, CheckCircle } from 'lucide-react';
import './StakeholderVerification.css';

const StakeholderVerification = ({ data, onChange, validationErrors = {} }) => {
  const handleMultiSelect = (field, value) => {
    const currentValues = data[field] || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    onChange(field, newValues);
  };

  const handleOtherFieldChange = (field, value) => {
    onChange(field, value);
  };

  // Helper function to get error class
  const getErrorClass = (fieldName) => {
    return validationErrors[fieldName] ? 'error' : '';
  };

  // Helper function to render error message
  const renderError = (fieldName) => {
    if (validationErrors[fieldName]) {
      return (
        <div className="error-message" role="alert">
          {validationErrors[fieldName]}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="step-content">
      <div className="step-header-section">
        {/* <Users className="section-icon" size={32} /> */}
        <h2>ثامناً: التحقق من البيانات الشخصية للمساهمين والملاك، أطراف أخرى</h2>
      </div>
      
      {/* Shareholders Verification Question */}
      <div className="">
        <div className="question-header">
          <span className="question-number">29</span>
          <label className="question-title">
            هل شركتكم بحاجة للتحقق من البيانات الشخصية للمساهمين أو الملاك؟
            <span className="required-indicator">*</span>
          </label>
        </div>
        
        <div className="data-category">
          {/* <div className="category-header">
            <Building2 className="category-icon" size={24} />
            <h4>التحقق من بيانات المساهمين:</h4>
          </div> */}
          
          <div className={`conditional-radio-grid ${getErrorClass('shareholdersVerification')}`}>
            <label className="conditional-radio-option shareholders-yes-option">
              <input
                type="radio"
                name="shareholdersVerification"
                value="نعم"
                checked={data.shareholdersVerification === 'نعم'}
                onChange={(e) => onChange('shareholdersVerification', e.target.value)}
              />
              <span className="radio-custom" aria-hidden="true"></span>
              <div className="radio-content">
                <span className="radio-text">نعم</span>
              </div>
            </label>
            
            <label className="conditional-radio-option shareholders-no-option">
              <input
                type="radio"
                name="shareholdersVerification"
                value="لا"
                checked={data.shareholdersVerification === 'لا'}
                onChange={(e) => onChange('shareholdersVerification', e.target.value)}
              />
              <span className="radio-custom" aria-hidden="true"></span>
              <div className="radio-content">
                <span className="radio-text">لا</span>
              </div>
            </label>
          </div>
          {renderError('shareholdersVerification')}
        </div>
      </div>

      {/* Shareholders Situations Question - Conditional */}
      {data.shareholdersVerification === 'نعم' && (
        <div className="">
          <div className="question-header">
            <span className="question-number">30</span>
            <label className="question-title">
              شركتنا بحاجة للتحقق من البيانات الشخصية للمساهمين أو الملاك في أي الحالات:
              <span className="required-indicator">*</span>
              <span className="multiple-choice-note">(يمكن اختيار أكثر من إجابة)</span>
            </label>
          </div>
          
          <div className="data-category" data-field="shareholdersSituations">
            {/* <div className="category-header">
              <CheckCircle className="category-icon" size={24} />
              <h4>حالات التحقق من بيانات المساهمين:</h4>
            </div> */}
            
            <div className={`checkbox-grid ${getErrorClass('shareholdersSituations')}`}>
              {[
                'عند تأسيس الشركة',
                'عند زيادة رأس المال أو دخول مساهمين جدد',
                'عند تغيير هيكل الملكية (بيع/شراء حصص)',
                'عند الحاجة للامتثال للمتطلبات النظامية المعتمدة',
                'عند التعامل مع جهات رقابية أو حكومية تتطلب ذلك',
                'عند إجراء صفقات أو شراكات استراتيجية كبيرة',
                'عند تقييم المخاطر التشغيلية أو السمعة',
                'بشكل دوري كجزء من إجراءات الحوكمة'
              ].map(situation => (
                <label key={situation} className="checkbox-item shareholders-situation-item">
                  <input
                    type="checkbox"
                    checked={(data.shareholdersSituations || []).includes(situation)}
                    onChange={() => handleMultiSelect('shareholdersSituations', situation)}
                  />
                  <span className="checkbox-custom" aria-hidden="true"></span>
                  <span className="checkbox-text">{situation}</span>
                </label>
              ))}
            </div>
            {renderError('shareholdersSituations')}
            
            <div className="other-input-group">
              <label className="other-label" htmlFor="other-shareholders-situations">أخرى (يرجى ذكرها):</label>
              <input
                id="other-shareholders-situations"
                type="text"
                value={data.otherShareholdersSituations || ''}
                onChange={(e) => handleOtherFieldChange('otherShareholdersSituations', e.target.value)}
                placeholder="حالات أخرى للتحقق من بيانات المساهمين..."
                className={`other-input ${getErrorClass('otherShareholdersSituations')}`}
              />
              {renderError('otherShareholdersSituations')}
            </div>
          </div>
        </div>
      )}

      {/* Suppliers Verification Question */}
      <div className="">
        <div className="question-header">
          <span className="question-number">31</span>
          <label className="question-title">
            هل شركتكم بحاجة للتحقق من البيانات الشخصية للموردين أو ممثليهم؟
            <span className="required-indicator">*</span>
          </label>
        </div>
        
        <div className="data-category">
          {/* <div className="category-header">
            <Truck className="category-icon" size={24} />
            <h4>التحقق من بيانات الموردين:</h4>
          </div> */}
          
          <div className={`conditional-radio-grid ${getErrorClass('suppliersVerification')}`}>
            <label className="conditional-radio-option suppliers-yes-option">
              <input
                type="radio"
                name="suppliersVerification"
                value="نعم"
                checked={data.suppliersVerification === 'نعم'}
                onChange={(e) => onChange('suppliersVerification', e.target.value)}
              />
              <span className="radio-custom" aria-hidden="true"></span>
              <div className="radio-content">
                <span className="radio-text">نعم</span>
                {/* <span className="radio-instruction">(انتقل إلى السؤال 32)</span> */}
              </div>
            </label>
            
            <label className="conditional-radio-option suppliers-no-option">
              <input
                type="radio"
                name="suppliersVerification"
                value="لا"
                checked={data.suppliersVerification === 'لا'}
                onChange={(e) => onChange('suppliersVerification', e.target.value)}
              />
              <span className="radio-custom" aria-hidden="true"></span>
              <div className="radio-content">
                <span className="radio-text">لا</span>
                {/* <span className="radio-instruction">(انتقل إلى السؤال 33)</span> */}
              </div>
            </label>
          </div>
          {renderError('suppliersVerification')}
        </div>
      </div>

      {/* Suppliers Situations Question - Conditional */}
      {data.suppliersVerification === 'نعم' && (
        <div className="">
          <div className="question-header">
            <span className="question-number">32</span>
            <label className="question-title">
              شركتنا بحاجة للتحقق من البيانات الشخصية للشركاء (كالموردين، المقاولين، الموزعين، مسؤولي الصيانة، أو ممثليهم) في الحالات التالية:
              <span className="required-indicator">*</span>
              <span className="multiple-choice-note">(يمكن اختيار أكثر من إجابة)</span>
            </label>
          </div>
          
          <div className="data-category" data-field="suppliersSituations">
            {/* <div className="category-header">
              <CheckCircle className="category-icon" size={24} />
              <h4>حالات التحقق من بيانات الشركاء:</h4>
            </div> */}
            
            <div className={`checkbox-grid ${getErrorClass('suppliersSituations')}`}>
              {[
                'عند التعاقد مع موردين جدد',
                'عند التعامل مع موردين لخدمات أو منتجات حساسة (مثل تقنية المعلومات، الأمن، الخدمات المالية)',
                'عند وجود متطلبات قانونية أو تنظيمية (مكافحة الفساد، حماية البيانات)',
                'عند تجديد العقود مع الموردين',
                'عند وجود شبهة تضارب مصالح أو ممارسات غير أخلاقية',
                'عند تقييم المخاطر المتعلقة بسلسلة التوريد',
                'بشكل دوري كجزء من إدارة مخاطر الموردين'
              ].map(situation => (
                <label key={situation} className="checkbox-item suppliers-situation-item">
                  <input
                    type="checkbox"
                    checked={(data.suppliersSituations || []).includes(situation)}
                    onChange={() => handleMultiSelect('suppliersSituations', situation)}
                  />
                  <span className="checkbox-custom" aria-hidden="true"></span>
                  <span className="checkbox-text">{situation}</span>
                </label>
              ))}
            </div>
            {renderError('suppliersSituations')}
            
            <div className="other-input-group">
              <label className="other-label" htmlFor="other-suppliers-situations">أخرى (يرجى ذكرها):</label>
              <input
                id="other-suppliers-situations"
                type="text"
                value={data.otherSuppliersSituations || ''}
                onChange={(e) => handleOtherFieldChange('otherSuppliersSituations', e.target.value)}
                placeholder="حالات أخرى للتحقق من بيانات الشركاء..."
                className={`other-input ${getErrorClass('otherSuppliersSituations')}`}
              />
              {renderError('otherSuppliersSituations')}
            </div>
          </div>
        </div>
      )}

      {/* Other Parties Question */}
      <div className="">
        <div className="question-header">
          <span className="question-number">33</span>
          <label className="question-title">
            شركتنا بحاجة للتحقق من البيانات الشخصية للأطراف التالية:
            <span className="required-indicator">*</span>
            <span className="multiple-choice-note">(يمكن اختيار أكثر من إجابة)</span>
          </label>
        </div>
        
        <div className="data-category" data-field="otherParties">
          {/* <div className="category-header">
            <UserCheck className="category-icon" size={24} />
            <h4>الأطراف الأخرى للتحقق:</h4>
          </div> */}
          
          <div className={`checkbox-grid ${getErrorClass('otherParties')}`}>
            {[
              'العملاء (في سياق الالتزام بمتطلبات "اعرف عميلك")',
              'الشركاء التجاريين أو الاستراتيجيين',
              'المقاولين أو الاستشاريين المستقلين',
              'أعضاء مجالس الإدارة واللجان',
              'الجهات الحكومية أو المنظمات التي نتعامل معها',
              'غير مهم'
            ].map(party => (
              <label key={party} className="checkbox-item other-parties-item">
                <input
                  type="checkbox"
                  checked={(data.otherParties || []).includes(party)}
                  onChange={() => handleMultiSelect('otherParties', party)}
                />
                <span className="checkbox-custom" aria-hidden="true"></span>
                <span className="checkbox-text">{party}</span>
              </label>
            ))}
          </div>
          {renderError('otherParties')}
          
          <div className="other-input-group">
            <label className="other-label" htmlFor="other-parties-custom">أخرى (يرجى ذكرها):</label>
            <input
              id="other-parties-custom"
              type="text"
              value={data.otherPartiesCustom || ''}
              onChange={(e) => handleOtherFieldChange('otherPartiesCustom', e.target.value)}
              placeholder="أطراف أخرى للتحقق..."
              className={`other-input ${getErrorClass('otherPartiesCustom')}`}
            />
            {renderError('otherPartiesCustom')}
          </div>
        </div>
      </div>

      {/* Verification Reasons Question */}
      <div className="">
        <div className="question-header">
          <span className="question-number">34</span>
          <label className="question-title">
            شركتنا بحاجة للقيام بعمليات التحقق من البيانات الشخصية للأطراف المذكورة أعلاه للأسباب التالية:
            <span className="required-indicator">*</span>
            <span className="multiple-choice-note">(يمكن اختيار أكثر من إجابة)</span>
          </label>
        </div>
        
        <div className="data-category" data-field="verificationReasons">
          {/* <div className="category-header">
            <Shield className="category-icon" size={24} />
            <h4>أسباب التحقق:</h4>
          </div> */}
          
          <div className={`checkbox-grid ${getErrorClass('verificationReasons')}`}>
            {[
              'الامتثال للمتطلبات القانونية والتنظيمية',
              'تقليل المخاطر المالية والتشغيلية',
              'حماية سمعة الشركة ومكانتها',
              'تعزيز الثقة مع الأطراف المعنية',
              'ضمان النزاهة والشفافية في التعاملات',
              'متطلبات داخلية للحوكمة والرقابة'
            ].map(reason => (
              <label key={reason} className="checkbox-item verification-reasons-item">
                <input
                  type="checkbox"
                  checked={(data.verificationReasons || []).includes(reason)}
                  onChange={() => handleMultiSelect('verificationReasons', reason)}
                />
                <span className="checkbox-custom" aria-hidden="true"></span>
                <span className="checkbox-text">{reason}</span>
              </label>
            ))}
          </div>
          {renderError('verificationReasons')}
          
          <div className="other-input-group">
            <label className="other-label" htmlFor="other-verification-reasons">أخرى (يرجى ذكرها):</label>
            <input
              id="other-verification-reasons"
              type="text"
              value={data.otherVerificationReasons || ''}
              onChange={(e) => handleOtherFieldChange('otherVerificationReasons', e.target.value)}
              placeholder="أسباب أخرى للتحقق..."
              className={`other-input ${getErrorClass('otherVerificationReasons')}`}
            />
            {renderError('otherVerificationReasons')}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StakeholderVerification;